import { isLeft, isRight } from 'fp-ts/Either';
import {
  ArrowUpRight,
  Check,
  Download,
  ExternalLink,
  LoaderCircle,
  Pin,
  Play,
  RefreshCw,
  Search,
  TriangleAlert,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { useAsyncData } from '../hooks/useAsyncData';
import { usePageTitle } from '../hooks/usePageTitle';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useTRPC } from '../hooks/useTRPC';
import { downloadFile } from '../lib/formatters';
import { cn } from '../lib/utils';
import type { RightResult, RouterOutputs } from '../types/TRPC';
import { captureAndReportError } from '../utils/errorReporting';

type RunsPage = RightResult<
  RouterOutputs['dappAnalytics']['listDappRankingRuns']
>;
type RunSummary = RunsPage['runs'][number];
type RunDetail = RightResult<
  RouterOutputs['dappAnalytics']['getDappRankingRun']
>;
type RunStatus = RunSummary['status'];
type Snapshot = NonNullable<RunDetail['snapshot']>;
type RankedApp = Snapshot['apps'][number];
type CategoryList = RightResult<
  RouterOutputs['dappAnalytics']['listDappRankingCategories']
>;
type CategoryState = {
  data: CategoryList | null;
  isLoading: boolean;
  error: string | null;
  request: () => void;
};

const UNCATEGORIZED = '__uncategorized__';
const RANKING_RUN_TIMEOUT_MS = 20 * 60 * 1000;

const EMPTY_PAGE: RunsPage = {
  environment: {
    targetEnvironment: 'test',
    storeOrigin: '',
  },
  control: {
    id: 'global',
    enabled: false,
    applyEnabled: false,
    updatedAt: null,
    updatedByUserId: null,
  },
  overrides: [],
  appliedRun: null,
  runs: [],
  nextCursor: null,
};

const number = new Intl.NumberFormat();
const decimal = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const oneDecimal = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
});
const dateTime = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});
const shortDateTime = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

function formatDate(value: string | null): string {
  return value ? dateTime.format(new Date(value)) : 'Never';
}

function formatShort(value: string): string {
  return shortDateTime.format(new Date(value));
}

const STATUS: Record<RunStatus, { dot: string; text: string; badge: string }> =
  {
    running: {
      dot: 'bg-sky-400 animate-pulse',
      text: 'text-sky-300',
      badge: 'border-sky-400/30 bg-sky-400/10 text-sky-200',
    },
    computed: {
      dot: 'bg-neutral-400',
      text: 'text-neutral-300',
      badge: 'border-white/10 bg-white/[0.06] text-neutral-200',
    },
    applied: {
      dot: 'bg-emerald-400',
      text: 'text-emerald-300',
      badge: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200',
    },
    rejected: {
      dot: 'bg-rose-400',
      text: 'text-rose-300',
      badge: 'border-rose-400/30 bg-rose-400/10 text-rose-200',
    },
    failed: {
      dot: 'bg-rose-400',
      text: 'text-rose-300',
      badge: 'border-rose-400/30 bg-rose-400/10 text-rose-200',
    },
  };

const COMPONENTS = [
  {
    key: 'quality',
    weightKey: 'qualityWeight',
    label: 'Quality',
    color: 'bg-teal-400',
  },
  {
    key: 'recentUsage',
    weightKey: 'activeWeight',
    label: 'Recent usage',
    color: 'bg-sky-400',
  },
  {
    key: 'installPopularity',
    weightKey: 'installWeight',
    label: 'Install popularity',
    color: 'bg-indigo-400',
  },
  {
    key: 'discovery',
    weightKey: 'discoveryWeight',
    label: 'Discovery',
    color: 'bg-fuchsia-400',
  },
] as const;
const WEIGHT_KEYS = new Set<string>(COMPONENTS.map((part) => part.weightKey));
const RATINGS = [1, 2, 3, 4, 5] as const;

const AGE_SOURCE: Record<RankedApp['ageSource'], string> = {
  portal_created: 'portal age',
  portal_first_publish: 'first publish',
  unknown_age_no_bonus: 'age unknown',
};

const RUN_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'applied', label: 'Applied' },
  { key: 'running', label: 'Running' },
  { key: 'problem', label: 'Problems' },
] as const;
type RunFilter = (typeof RUN_FILTERS)[number]['key'];

function matchesFilter(status: RunStatus, filter: RunFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'problem') return status === 'failed' || status === 'rejected';
  return status === filter;
}

const DETAIL_TABS = ['Overview', 'Ranked apps', 'Raw JSON'] as const;
type DetailTab = (typeof DETAIL_TABS)[number];

const EYEBROW =
  'text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500';
const FIELD =
  'rounded-lg border border-white/[0.08] bg-white/[0.03] text-neutral-100 placeholder:text-neutral-600 focus:border-teal-400/50 focus:outline-none focus:ring-2 focus:ring-teal-400/20 disabled:opacity-50';
const PRIMARY_BUTTON =
  'bg-white text-neutral-950 shadow-none hover:bg-neutral-200';
const SOFT_BUTTON =
  'border border-white/[0.08] bg-white/[0.03] text-neutral-200 hover:bg-white/[0.08] hover:text-white';
const QUIET_BUTTON = 'text-neutral-400 hover:bg-white/[0.06] hover:text-white';

function scoreParts(components: RankedApp['components']) {
  return COMPONENTS.map((part) => ({
    label: part.label,
    value: components[part.key],
    color: part.color,
  }));
}

function weightParts(formula: Snapshot['formula']) {
  return COMPONENTS.map((part) => ({
    label: part.label,
    value: formula[part.weightKey] ?? 0,
    color: part.color,
  }));
}

function Panel({
  title,
  aside,
  className,
  children,
}: {
  title?: string;
  aside?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        'flex flex-col rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5',
        className,
      )}
    >
      {title && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className={EYEBROW}>{title}</h2>
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

function Note({
  tone = 'error',
  children,
}: {
  tone?: 'error' | 'busy';
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border px-4 py-3 text-sm',
        tone === 'error'
          ? 'border-rose-500/20 bg-rose-500/[0.06] text-rose-200'
          : 'border-sky-400/20 bg-sky-400/[0.06] text-sky-100',
      )}
    >
      {tone === 'error' ? (
        <TriangleAlert className="mt-0.5 size-4 shrink-0" />
      ) : (
        <LoaderCircle className="mt-0.5 size-4 shrink-0 animate-spin" />
      )}
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}

function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn('animate-pulse rounded-md bg-white/[0.06]', className)}
    />
  );
}

// Renders every possible label stacked in one grid cell so a button keeps the
// width of its widest state instead of resizing as the label changes.
function StableLabel({
  options,
  active,
}: {
  options: readonly string[];
  active: string;
}) {
  return (
    <span className="grid">
      {options.map((option) => (
        <span
          key={option}
          aria-hidden={option !== active}
          className={cn(
            'col-start-1 row-start-1 whitespace-nowrap text-center',
            option !== active && 'invisible',
          )}
        >
          {option}
        </span>
      ))}
    </span>
  );
}

function StatusBadge({ status, label }: { status: RunStatus; label?: string }) {
  const tone = STATUS[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        tone.badge,
      )}
    >
      <span aria-hidden className={cn('size-1.5 rounded-full', tone.dot)} />
      {label ?? status}
    </span>
  );
}

function Switch({
  checked,
  disabled,
  labelId,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  labelId: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-6 w-11 shrink-0 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/60 disabled:cursor-not-allowed disabled:opacity-40',
        checked
          ? 'border-teal-300/40 bg-teal-500'
          : 'border-white/10 bg-white/[0.08]',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'absolute left-0.5 top-0.5 size-[18px] rounded-full bg-white shadow-sm transition-transform',
          checked && 'translate-x-5',
        )}
      />
    </button>
  );
}

function SwitchRow({
  id,
  title,
  description,
  ...switchProps
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p id={id} className="text-sm font-medium text-neutral-100">
          {title}
        </p>
        <p className="mt-0.5 text-xs leading-5 text-neutral-500">
          {description}
        </p>
      </div>
      <Switch labelId={id} {...switchProps} />
    </div>
  );
}

function StackedBar({
  parts,
  max,
  className,
}: {
  parts: Array<{ label: string; value: number; color: string }>;
  max: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-1.5 overflow-hidden rounded-full bg-white/[0.06]',
        className,
      )}
      title={parts
        .map((part) => `${part.label}: ${decimal.format(part.value)}`)
        .join('\n')}
    >
      {max > 0 &&
        parts.map((part) => (
          <div
            key={part.label}
            className={part.color}
            style={{ width: `${(Math.max(part.value, 0) / max) * 100}%` }}
          />
        ))}
    </div>
  );
}

function RatingBars({ counts }: { counts: number[] }) {
  const peak = Math.max(...counts, 1);
  return (
    <div
      aria-hidden
      className="flex h-4 items-end gap-px"
      title={RATINGS.map(
        (stars) => `${stars}★ ${number.format(counts[stars - 1] ?? 0)}`,
      ).join('  ')}
    >
      {RATINGS.map((stars) => {
        const count = counts[stars - 1] ?? 0;
        return (
          <span
            key={stars}
            className={cn(
              'w-1 rounded-[1px]',
              count > 0 ? 'bg-neutral-400' : 'bg-neutral-800',
            )}
            style={{ height: `${Math.max((count / peak) * 100, 10)}%` }}
          />
        );
      })}
    </div>
  );
}

type Fact = { label: string; value: React.ReactNode; mono?: boolean };

function Facts({ rows }: { rows: Fact[] }) {
  return (
    <dl className="divide-y divide-white/[0.05]">
      {rows.map((row) => (
        <div
          key={row.label}
          className="grid grid-cols-[112px_minmax(0,1fr)] gap-3 py-2 text-sm first:pt-0 last:pb-0"
        >
          <dt className="text-neutral-500">{row.label}</dt>
          <dd
            className={cn(
              'min-w-0 break-all text-neutral-200',
              row.mono && 'font-mono text-xs leading-5',
            )}
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function RunRow({
  run,
  selected,
  live,
  onSelect,
}: {
  run: RunSummary;
  selected: boolean;
  live: boolean;
  onSelect: () => void;
}) {
  const tone = STATUS[run.status];
  const summary =
    run.appCount !== null
      ? `${number.format(run.appCount)} apps`
      : run.status === 'running'
        ? 'Computing…'
        : 'No snapshot';
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected}
      className={cn(
        'grid w-full grid-cols-[8px_minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-0.5 rounded-xl px-3 py-2.5 text-left transition-colors',
        selected ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]',
      )}
    >
      <span
        aria-hidden
        className={cn('size-2 self-center rounded-full', tone.dot)}
      />
      <span className="truncate font-mono text-[13px] text-neutral-100">
        {run.asOfDate}
        <span className="ml-1.5 font-sans text-xs text-neutral-500">
          attempt {run.attempt}
        </span>
      </span>
      <span
        className={cn(
          'text-[11px] font-medium',
          live ? 'text-emerald-300' : tone.text,
        )}
      >
        {live ? 'Live now' : run.status}
      </span>
      <span className="col-span-2 col-start-2 truncate text-xs text-neutral-500">
        {summary} · {formatShort(run.createdAt)}
      </span>
    </button>
  );
}

function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  const safe =
    typeof value === 'string' && /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function buildRankingRunCsv(snapshot: Snapshot): string {
  const rows = snapshot.apps.map((app) => ({
    run_id: snapshot.runId,
    run_key: snapshot.runKey,
    as_of_date: snapshot.asOfDate,
    generated_at: snapshot.generatedAt,
    formula_version: snapshot.formulaVersion,
    ranking_hash: snapshot.rankingHash,
    source_environment: snapshot.sourceEnvironment,
    target_environment: snapshot.targetEnvironment,
    installer_metric_as_of_date: snapshot.installerMetricAsOfDate,
    mau_as_of_date: snapshot.mauAsOfDate,
    formula_json: JSON.stringify(snapshot.formula),
    inactive_overrides: snapshot.inactiveOverrides
      .map((override) => `#${override.fixedRank} ${override.androidPackage}`)
      .join('; '),
    final_global_rank: app.finalGlobalRank,
    calculated_rank: app.calculatedRank,
    fixed_rank: app.fixedRank,
    store_weight: app.storeWeight,
    score: app.score,
    android_package: app.androidPackage,
    normalized_android_package: app.normalizedAndroidPackage,
    display_name: app.displayName,
    one_star_reviews: app.reviewsByRating[0],
    two_star_reviews: app.reviewsByRating[1],
    three_star_reviews: app.reviewsByRating[2],
    four_star_reviews: app.reviewsByRating[3],
    five_star_reviews: app.reviewsByRating[4],
    review_count: app.reviewCount,
    average_rating: app.averageRating,
    adjusted_rating: app.adjustedRating,
    observed_install_events: app.observedInstallEvents,
    distinct_installer_devices: app.distinctInstallerDevices,
    first_observed_install_at: app.firstObservedInstallAt,
    last_observed_install_at: app.lastObservedInstallAt,
    mau_30: app.mau30,
    app_mau_as_of_date: app.mauAsOfDate,
    days_since_first_published: app.daysSinceFirstPublished,
    age_source: app.ageSource,
    quality_component: app.components.quality,
    recent_usage_component: app.components.recentUsage,
    install_popularity_component: app.components.installPopularity,
    discovery_component: app.components.discovery,
    data_quality_flags: app.dataQualityFlags.join('; '),
  }));

  return [Object.keys(rows[0] ?? {}), ...rows.map((row) => Object.values(row))]
    .map((row) => row.map(csvCell).join(','))
    .join('\n');
}

function RankedAppsTable({
  apps,
  maxScore,
  categories,
}: {
  apps: Snapshot['apps'];
  maxScore: number;
  categories: CategoryState;
}) {
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState('');

  const { request } = categories;
  useEffect(() => {
    request();
  }, [request]);

  const packagesByCategory = useMemo(() => {
    const map = new Map<string, Set<string>>();
    const categorized = new Set<string>();
    for (const category of categories.data?.categories ?? []) {
      const members = new Set(
        category.androidPackages.map((value) => value.trim().toLowerCase()),
      );
      map.set(category.id, members);
      for (const member of members) categorized.add(member);
    }
    if (categories.data) {
      map.set(
        UNCATEGORIZED,
        new Set(
          apps
            .map((app) => app.normalizedAndroidPackage)
            .filter((value) => !categorized.has(value)),
        ),
      );
    }
    return map;
  }, [apps, categories.data]);

  const members = categoryId ? packagesByCategory.get(categoryId) : undefined;
  const needle = query.trim().toLowerCase();
  const visible = apps.filter(
    (app) =>
      (!members || members.has(app.normalizedAndroidPackage)) &&
      (!needle ||
        app.displayName.toLowerCase().includes(needle) ||
        app.androidPackage.toLowerCase().includes(needle)),
  );
  const inCategoryPosition = new Map(
    apps
      .filter((app) => !members || members.has(app.normalizedAndroidPackage))
      .map((app, index) => [app.normalizedAndroidPackage, index + 1]),
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="relative min-w-[220px] flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-500" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by name or package"
            aria-label="Filter ranked apps"
            className={cn(FIELD, 'h-9 w-full pl-9 pr-3 text-sm')}
          />
        </label>
        <select
          value={categoryId}
          onChange={(event) => setCategoryId(event.target.value)}
          disabled={!categories.data}
          aria-label="Category view"
          className={cn(FIELD, 'h-9 px-3 text-sm scheme-dark')}
        >
          <option value="">
            {categories.isLoading
              ? 'Loading categories…'
              : `All apps (${apps.length})`}
          </option>
          {(categories.data?.categories ?? []).map((category) => {
            const count = apps.filter((app) =>
              packagesByCategory
                .get(category.id)
                ?.has(app.normalizedAndroidPackage),
            ).length;
            return (
              <option key={category.id} value={category.id}>
                {category.name} ({count})
              </option>
            );
          })}
          {categories.data && (
            <option value={UNCATEGORIZED}>
              Uncategorized ({packagesByCategory.get(UNCATEGORIZED)?.size ?? 0})
            </option>
          )}
        </select>
        <span className="ml-auto text-xs tabular-nums text-neutral-500">
          {number.format(visible.length)} of {number.format(apps.length)} apps
        </span>
      </div>

      <p className="text-xs text-neutral-500">
        {categories.error ? (
          <span className="text-amber-300">
            {categories.error}{' '}
            <button
              type="button"
              onClick={() => categories.request()}
              className="underline underline-offset-2"
            >
              Retry
            </button>
          </span>
        ) : (
          "Category views keep this run's global order and filter it to the Store's live category membership."
        )}
      </p>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-[11px] text-neutral-500">
        {COMPONENTS.map((part) => (
          <span key={part.key} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className={cn('size-1.5 rounded-full', part.color)}
            />
            {part.label}
          </span>
        ))}
        <span className="ml-auto">
          Bars are scaled to the {number.format(maxScore)}-point maximum
        </span>
      </div>

      <div className="max-h-[70vh] overflow-auto rounded-2xl border border-white/[0.06] bg-neutral-950">
        <table className="w-full min-w-[1040px] text-left text-sm">
          <thead className="sticky top-0 z-10 bg-neutral-950 text-[11px] font-medium uppercase tracking-wider text-neutral-500">
            <tr className="border-b border-white/[0.06]">
              <th className="px-4 py-3">Rank</th>
              <th className="px-3 py-3">App</th>
              <th className="px-3 py-3">Score</th>
              <th className="px-3 py-3">Rating</th>
              <th className="px-3 py-3">Installs</th>
              <th className="px-3 py-3">Usage · age</th>
              <th className="px-3 py-3">Data</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.05]">
            {visible.map((app) => {
              const position = categoryId
                ? inCategoryPosition.get(app.normalizedAndroidPackage)
                : app.finalGlobalRank;
              return (
                <tr
                  key={app.normalizedAndroidPackage}
                  className="align-top transition-colors hover:bg-white/[0.03]"
                >
                  <td className="px-4 py-3.5">
                    <div className="text-lg font-semibold leading-none tabular-nums text-white">
                      {position}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-1.5 whitespace-nowrap text-[11px] tabular-nums text-neutral-500">
                      {categoryId && <span>global #{app.finalGlobalRank}</span>}
                      {app.calculatedRank !== app.finalGlobalRank && (
                        <span>calc #{app.calculatedRank}</span>
                      )}
                      {app.fixedRank !== null && (
                        <span className="inline-flex items-center gap-0.5 text-amber-300">
                          <Pin className="size-3" aria-hidden />
                          fixed #{app.fixedRank}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3.5">
                    <div className="font-medium text-neutral-100">
                      {app.displayName}
                    </div>
                    <code className="mt-0.5 block font-mono text-[11px] text-neutral-500">
                      {app.androidPackage}
                    </code>
                  </td>
                  <td className="px-3 py-3.5">
                    <div className="font-semibold tabular-nums text-neutral-100">
                      {oneDecimal.format(app.score)}
                    </div>
                    <StackedBar
                      parts={scoreParts(app.components)}
                      max={maxScore}
                      className="mt-1.5 w-28"
                    />
                    <div className="mt-1 text-[11px] tabular-nums text-neutral-600">
                      weight {number.format(app.storeWeight)}
                    </div>
                  </td>
                  <td className="px-3 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <span className="font-semibold tabular-nums text-neutral-100">
                        {app.averageRating === null
                          ? '–'
                          : decimal.format(app.averageRating)}
                      </span>
                      <RatingBars counts={app.reviewsByRating} />
                    </div>
                    <div className="mt-1 whitespace-nowrap text-[11px] tabular-nums text-neutral-500">
                      {number.format(app.reviewCount)} reviews · adj{' '}
                      {decimal.format(app.adjustedRating)}
                    </div>
                  </td>
                  <td className="px-3 py-3.5 tabular-nums">
                    <div className="text-neutral-100">
                      {number.format(app.distinctInstallerDevices)}
                      <span className="text-neutral-500"> devices</span>
                    </div>
                    <div className="mt-1 text-[11px] text-neutral-500">
                      {number.format(app.observedInstallEvents)} events
                    </div>
                    <div className="whitespace-nowrap font-mono text-[11px] text-neutral-600">
                      {app.firstObservedInstallAt ?? '–'} →{' '}
                      {app.lastObservedInstallAt ?? '–'}
                    </div>
                  </td>
                  <td className="px-3 py-3.5 tabular-nums">
                    <div className="text-neutral-100">
                      {number.format(app.mau30)}
                      <span className="text-neutral-500"> MAU</span>
                    </div>
                    <div className="mt-1 whitespace-nowrap text-[11px] text-neutral-500">
                      {app.daysSinceFirstPublished === null
                        ? 'Unknown age'
                        : `${number.format(app.daysSinceFirstPublished)} days`}
                      {' · '}
                      {AGE_SOURCE[app.ageSource]}
                    </div>
                  </td>
                  <td className="px-3 py-3.5">
                    {app.dataQualityFlags.length > 0 ? (
                      <div className="flex max-w-[220px] flex-wrap gap-1">
                        {app.dataQualityFlags.map((flag) => (
                          <span
                            key={flag}
                            className="rounded-md border border-amber-400/20 bg-amber-400/10 px-1.5 py-0.5 text-[11px] text-amber-200"
                          >
                            {flag.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                        <Check className="size-3.5" aria-hidden />
                        Complete
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {visible.length === 0 && (
          <p className="p-6 text-center text-sm text-neutral-500">
            {needle ? `No apps match “${query}”.` : 'No apps in this view.'}
          </p>
        )}
      </div>
    </div>
  );
}

function RunDetails({
  run,
  categories,
  applying,
  live,
  tab,
  onTabChange,
  onApply,
}: {
  run: RunDetail;
  categories: CategoryState;
  applying: boolean;
  live: boolean;
  tab: DetailTab;
  onTabChange: (tab: DetailTab) => void;
  onApply: () => void;
}) {
  const snapshot = run.snapshot;
  const weights = snapshot ? weightParts(snapshot.formula) : [];
  const maxScore = weights.reduce((sum, part) => sum + part.value, 0);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-mono text-xl font-semibold tracking-tight text-white">
              {run.asOfDate}
            </h2>
            <span className="text-sm text-neutral-500">
              attempt {run.attempt}
            </span>
            <StatusBadge
              status={run.status}
              label={live ? 'Live now' : undefined}
            />
          </div>
          <p className="mt-1.5 break-all font-mono text-xs text-neutral-500">
            {run.runKey}
          </p>
        </div>
        {snapshot && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Button
              variant="ghost"
              size="sm"
              className={SOFT_BUTTON}
              onClick={() =>
                downloadFile(
                  buildRankingRunCsv(snapshot),
                  `dapp-ranking-${run.asOfDate}-attempt-${run.attempt}.csv`,
                  'text/csv;charset=utf-8',
                )
              }
            >
              <Download aria-hidden />
              Export CSV
            </Button>
            {(run.status === 'computed' || run.status === 'applied') && (
              <Button
                size="sm"
                className={PRIMARY_BUTTON}
                disabled={applying}
                onClick={onApply}
              >
                {applying ? (
                  <LoaderCircle className="animate-spin" aria-hidden />
                ) : (
                  <ArrowUpRight aria-hidden />
                )}
                <StableLabel
                  options={['Apply this run to Store', 'Applying…']}
                  active={applying ? 'Applying…' : 'Apply this run to Store'}
                />
              </Button>
            )}
          </div>
        )}
      </header>

      {run.error && <Note>{run.error}</Note>}

      {!snapshot ? (
        run.status === 'running' ? (
          <Note tone="busy">
            Ranking inputs are being computed. This view refreshes
            automatically.
          </Note>
        ) : (
          <p className="text-sm text-neutral-400">
            No snapshot was produced for this run.
          </p>
        )
      ) : (
        <>
          <nav
            aria-label="Run detail sections"
            className="border-b border-white/[0.06] text-sm"
          >
            <div className="-mb-px flex gap-1">
              {DETAIL_TABS.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => onTabChange(name)}
                  aria-current={tab === name}
                  className={cn(
                    'inline-flex items-center gap-1.5 border-b-2 px-3 py-2.5 font-medium transition-colors',
                    tab === name
                      ? 'border-teal-400 text-white'
                      : 'border-transparent text-neutral-500 hover:text-neutral-200',
                  )}
                >
                  {name}
                  {name === 'Ranked apps' && (
                    <span className="rounded-full bg-white/[0.08] px-1.5 py-0.5 text-[11px] tabular-nums text-neutral-300">
                      {number.format(snapshot.apps.length)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </nav>

          {tab === 'Overview' ? (
            <div className="grid gap-4 xl:grid-cols-3">
              <Panel title="Score anatomy" className="xl:col-span-3">
                <StackedBar parts={weights} max={maxScore} className="h-2.5" />
                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {weights.map((part) => (
                    <div key={part.label}>
                      <div className="flex items-center gap-1.5 text-xs text-neutral-400">
                        <span
                          aria-hidden
                          className={cn('size-1.5 rounded-full', part.color)}
                        />
                        {part.label}
                      </div>
                      <div className="mt-1 text-xl font-semibold tabular-nums text-white">
                        {decimal.format(part.value)}
                        <span className="ml-1 text-xs font-normal text-neutral-500">
                          pts
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-1.5 border-t border-white/[0.06] pt-4">
                  <span className="mr-1 text-[11px] text-neutral-500">
                    {snapshot.formulaVersion}
                  </span>
                  {Object.entries(snapshot.formula)
                    .filter(([key]) => !WEIGHT_KEYS.has(key))
                    .map(([key, value]) => (
                      <span
                        key={key}
                        className="rounded-md border border-white/[0.06] bg-white/[0.03] px-2 py-1 font-mono text-[11px] text-neutral-500"
                      >
                        {key}{' '}
                        <span className="text-neutral-200">
                          {decimal.format(value)}
                        </span>
                      </span>
                    ))}
                </div>
              </Panel>

              <Panel title="Run">
                <Facts
                  rows={[
                    { label: 'Run ID', value: run.id, mono: true },
                    { label: 'Formula', value: run.formulaVersion, mono: true },
                    {
                      label: 'Ranking hash',
                      value: run.rankingHash ?? '–',
                      mono: true,
                    },
                  ]}
                />
              </Panel>
              <Panel title="Inputs">
                <Facts
                  rows={[
                    {
                      label: 'Apps ranked',
                      value: number.format(snapshot.apps.length),
                    },
                    { label: 'Source env', value: snapshot.sourceEnvironment },
                    {
                      label: 'Store target',
                      value: snapshot.targetEnvironment,
                    },
                    {
                      label: 'Installer data',
                      value: snapshot.installerMetricAsOfDate,
                    },
                    { label: 'MAU data', value: snapshot.mauAsOfDate },
                    {
                      label: 'Inactive overrides',
                      value:
                        snapshot.inactiveOverrides.length === 0
                          ? 'None'
                          : snapshot.inactiveOverrides
                              .map(
                                (override) =>
                                  `#${override.fixedRank} ${override.androidPackage}`,
                              )
                              .join(', '),
                    },
                  ]}
                />
              </Panel>
              <Panel title="Timeline">
                <Facts
                  rows={[
                    {
                      label: 'Generated',
                      value: formatDate(snapshot.generatedAt),
                    },
                    { label: 'Applied', value: formatDate(run.appliedAt) },
                    { label: 'Last updated', value: formatDate(run.updatedAt) },
                  ]}
                />
              </Panel>
            </div>
          ) : tab === 'Ranked apps' ? (
            <RankedAppsTable
              apps={snapshot.apps}
              maxScore={maxScore}
              categories={categories}
            />
          ) : (
            <pre className="max-h-[70vh] overflow-auto rounded-2xl border border-white/[0.06] bg-neutral-950 p-4 font-mono text-[11px] leading-relaxed text-neutral-400">
              {JSON.stringify(snapshot, null, 2)}
            </pre>
          )}
        </>
      )}
    </div>
  );
}

export function DappRankingAdminPage() {
  const { canRender } = useRequireAuth({ requireRole: 'superadmin' });
  const trpc = useTRPC();
  const computeLabelId = useId();
  const applyLabelId = useId();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailTab>('Overview');
  const [enabled, setEnabled] = useState(false);
  const [applyEnabled, setApplyEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [applyingRunId, setApplyingRunId] = useState<string | null>(null);
  const [overridePackage, setOverridePackage] = useState('');
  const [fixedRank, setFixedRank] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);
  const [filter, setFilter] = useState<RunFilter>('all');
  const {
    data: page,
    setData: setPage,
    isLoading,
    error,
    run: runPage,
  } = useAsyncData<RunsPage>({ initialData: EMPTY_PAGE, initialLoading: true });
  const {
    data: runDetail,
    isLoading: detailLoading,
    error: detailError,
    run: runDetailRequest,
  } = useAsyncData<RunDetail | null>({ initialData: null });
  const {
    data: categories,
    isLoading: categoriesLoading,
    error: categoriesError,
    run: runCategories,
  } = useAsyncData<CategoryList | null>({ initialData: null });
  const categoriesRequested = useRef(false);
  const lastRequestedRunId = useRef<string | null>(null);

  usePageTitle('Dynamic Rankings | Publishing Portal');

  // `quiet` refreshes (polling) skip the loading flag so nothing flickers.
  const loadRuns = useCallback(
    async (cursor?: string, quiet = false) => {
      await runPage(
        () =>
          trpc.dappAnalytics.listDappRankingRuns.query({ cursor, limit: 50 }),
        {
          showLoadingState: !quiet,
          onSuccess: (result, helpers) => {
            if (isLeft(result)) {
              helpers.setError(result.left.message);
              return;
            }
            helpers.setData((current) => ({
              ...result.right,
              runs: cursor
                ? [...current.runs, ...result.right.runs]
                : result.right.runs,
            }));
            if (!cursor) {
              setEnabled(result.right.control.enabled);
              setApplyEnabled(result.right.control.applyEnabled);
              setSelectedRunId(
                (current) => current ?? result.right.runs[0]?.id ?? null,
              );
            }
          },
          onError: (loadError, helpers) => {
            captureAndReportError(loadError, {
              operation: 'superadmin.dappRanking.listRuns',
              component: 'DappRankingAdminPage',
            });
            helpers.setError('Failed to load dynamic ranking runs');
          },
        },
      );
    },
    [runPage, trpc],
  );

  const loadRunDetail = useCallback(
    async (id: string, quiet = false) => {
      await runDetailRequest(
        () => trpc.dappAnalytics.getDappRankingRun.query({ id }),
        {
          showLoadingState: !quiet,
          onSuccess: (result, helpers) => {
            if (isRight(result)) {
              helpers.setData(result.right);
            } else {
              helpers.setData(null);
              helpers.setError(result.left.message);
            }
          },
          onError: (loadError, helpers) => {
            captureAndReportError(loadError, {
              operation: 'superadmin.dappRanking.getRun',
              component: 'DappRankingAdminPage',
              runId: id,
            });
            helpers.setData(null);
            helpers.setError('Failed to load ranking run details');
          },
        },
      );
    },
    [runDetailRequest, trpc],
  );

  const loadCategories = useCallback(() => {
    if (categoriesRequested.current) return;
    categoriesRequested.current = true;
    void runCategories(
      () => trpc.dappAnalytics.listDappRankingCategories.query({}),
      {
        onSuccess: (result, helpers) => {
          if (isRight(result)) {
            helpers.setData(result.right);
          } else {
            categoriesRequested.current = false;
            helpers.setError(result.left.message);
          }
        },
        onError: (loadError, helpers) => {
          categoriesRequested.current = false;
          captureAndReportError(loadError, {
            operation: 'superadmin.dappRanking.listCategories',
            component: 'DappRankingAdminPage',
          });
          helpers.setError('Failed to load store categories');
        },
      },
    );
  }, [runCategories, trpc]);

  const categoryState = useMemo(
    () => ({
      data: categories,
      isLoading: categoriesLoading,
      error: categoriesError,
      request: loadCategories,
    }),
    [categories, categoriesLoading, categoriesError, loadCategories],
  );

  const selectedRunUpdatedAt = page.runs.find(
    (run) => run.id === selectedRunId,
  )?.updatedAt;

  useEffect(() => {
    if (canRender) void loadRuns();
  }, [canRender, loadRuns]);

  useEffect(() => {
    if (!canRender || !selectedRunId) return;
    // The previous detail stays on screen (dimmed) while a newly selected run
    // loads; re-fetching the same run after a poll is silent.
    const quiet = lastRequestedRunId.current === selectedRunId;
    lastRequestedRunId.current = selectedRunId;
    void loadRunDetail(selectedRunId, quiet);
  }, [canRender, loadRunDetail, selectedRunId, selectedRunUpdatedAt]);

  const runInProgress = page.runs.some(
    (run) =>
      run.status === 'running' &&
      Date.now() - new Date(run.updatedAt).getTime() < RANKING_RUN_TIMEOUT_MS,
  );

  useEffect(() => {
    if (!canRender || !runInProgress) return;
    const timeout = window.setTimeout(
      () => void loadRuns(undefined, true),
      5_000,
    );
    return () => window.clearTimeout(timeout);
  }, [canRender, loadRuns, runInProgress]);

  const visibleRuns = useMemo(
    () => page.runs.filter((run) => matchesFilter(run.status, filter)),
    [page.runs, filter],
  );

  const saveControl = async () => {
    setSaving(true);
    try {
      const result = await trpc.dappAnalytics.setDappRankingControl.mutate({
        enabled,
        applyEnabled,
      });
      if (isLeft(result)) {
        toast.error(result.left.message);
        return;
      }
      setPage((current) => ({ ...current, control: result.right }));
      toast.success('Dynamic ranking controls updated');
    } catch (saveError) {
      captureAndReportError(saveError, {
        operation: 'superadmin.dappRanking.setControl',
        component: 'DappRankingAdminPage',
      });
      toast.error('Failed to update dynamic ranking controls');
    } finally {
      setSaving(false);
    }
  };

  const triggerRankingRun = async () => {
    setTriggering(true);
    try {
      const result = await trpc.dappAnalytics.triggerDappRankingRun.mutate({});
      if (isLeft(result)) {
        toast.error(result.left.message);
        return;
      }
      toast.success('Ranking run requested');
      await loadRuns();
    } catch (triggerError) {
      captureAndReportError(triggerError, {
        operation: 'superadmin.dappRanking.triggerRun',
        component: 'DappRankingAdminPage',
      });
      toast.error('Failed to request a ranking run');
    } finally {
      setTriggering(false);
    }
  };

  const applyRankingRun = async (run: RunDetail) => {
    if (
      !window.confirm(
        `Apply ${run.runKey} to the ${run.targetEnvironment} dApp Store?`,
      )
    ) {
      return;
    }
    setApplyingRunId(run.id);
    try {
      const result = await trpc.dappAnalytics.applyDappRankingRun.mutate({
        id: run.id,
      });
      if (isLeft(result)) {
        toast.error(result.left.message);
        return;
      }
      toast.success('Ranking run applied to the dApp Store');
      await loadRuns();
    } catch (applyError) {
      captureAndReportError(applyError, {
        operation: 'superadmin.dappRanking.applyRun',
        component: 'DappRankingAdminPage',
        runId: run.id,
      });
      toast.error('Failed to apply ranking run');
    } finally {
      setApplyingRunId(null);
    }
  };

  const saveRankingOverride = async (value: number | null) => {
    setSavingOverride(true);
    try {
      const result = await trpc.dappAnalytics.setDappRankingOverride.mutate({
        androidPackage: overridePackage,
        fixedRank: value,
      });
      if (isLeft(result)) {
        toast.error(result.left.message);
        return;
      }
      if (value === null) setFixedRank('');
      toast.success(value === null ? 'Fixed rank cleared' : 'Fixed rank saved');
      await loadRuns();
    } catch (overrideError) {
      captureAndReportError(overrideError, {
        operation: 'superadmin.dappRanking.setOverride',
        component: 'DappRankingAdminPage',
        androidPackage: overridePackage,
      });
      toast.error('Failed to update the fixed rank');
    } finally {
      setSavingOverride(false);
    }
  };

  if (!canRender) return null;

  const dirty =
    enabled !== page.control.enabled ||
    applyEnabled !== page.control.applyEnabled;
  const production = page.environment.targetEnvironment === 'production';
  // Keep the inspector skeleton up from first paint until a selection has
  // loaded, so it never flips between placeholder, skeleton and content.
  const detailPending =
    !runDetail &&
    !detailError &&
    (detailLoading ||
      selectedRunId !== null ||
      (isLoading && page.runs.length === 0));
  const packageEntered = overridePackage.trim().length > 0;
  const rankValid =
    Number.isInteger(Number(fixedRank)) && Number(fixedRank) >= 1;

  return (
    <main className="relative flex-1 bg-neutral-950 text-neutral-200">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(70%_100%_at_50%_0%,rgba(45,212,191,0.07),transparent)]"
      />
      <div className="relative mx-auto max-w-[1440px] px-4 pb-16 pt-8 md:px-8">
        <header className="flex flex-wrap items-end justify-between gap-6">
          <div className="min-w-0">
            <p className={cn(EYEBROW, 'text-teal-400/90')}>Dynamic rankings</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-white">
              Store rankings
            </h1>
            <p className="mt-2 flex min-h-6 flex-wrap items-center gap-2 text-sm text-neutral-400">
              <span>Target</span>
              {page.environment.storeOrigin ? (
                <>
                  <span
                    className={cn(
                      'rounded-full border px-2.5 py-0.5 text-xs font-medium',
                      production
                        ? 'border-amber-400/30 bg-amber-400/10 text-amber-200'
                        : 'border-white/10 bg-white/[0.06] text-neutral-300',
                    )}
                  >
                    {page.environment.targetEnvironment} dApp Store
                  </span>
                  <a
                    href={page.environment.storeOrigin}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-xs text-neutral-400 transition-colors hover:text-teal-300"
                  >
                    {page.environment.storeOrigin}
                    <ExternalLink className="size-3" aria-hidden />
                  </a>
                </>
              ) : (
                <span className="text-neutral-500">
                  {isLoading ? 'Loading…' : 'Unavailable'}
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Button
              variant="ghost"
              size="sm"
              className={QUIET_BUTTON}
              disabled={isLoading}
              onClick={() => void loadRuns()}
            >
              <RefreshCw
                className={cn(isLoading && 'animate-spin')}
                aria-hidden
              />
              Refresh
            </Button>
            <Button
              size="sm"
              className={PRIMARY_BUTTON}
              disabled={triggering || runInProgress || dirty || isLoading}
              title={
                dirty ? 'Save or discard the pipeline changes first' : undefined
              }
              onClick={() => void triggerRankingRun()}
            >
              {runInProgress || triggering ? (
                <LoaderCircle className="animate-spin" aria-hidden />
              ) : (
                <Play aria-hidden />
              )}
              <StableLabel
                options={['Run rankings now', 'Run in progress', 'Requesting…']}
                active={
                  runInProgress
                    ? 'Run in progress'
                    : triggering
                      ? 'Requesting…'
                      : 'Run rankings now'
                }
              />
            </Button>
          </div>
        </header>

        {error && (
          <div className="mt-6">
            <Note>{error}</Note>
          </div>
        )}

        <section className="mt-8 grid gap-4 lg:grid-cols-3">
          <Panel
            title="Live on the Store"
            className={cn(
              page.appliedRun && 'border-emerald-400/20 bg-emerald-400/[0.04]',
            )}
          >
            {page.appliedRun ? (
              <div className="flex flex-1 flex-col">
                <code className="break-all font-mono text-sm font-medium text-emerald-200">
                  {page.appliedRun.runKey}
                </code>
                <code className="mt-1 break-all font-mono text-[11px] text-neutral-500">
                  Run ID: {page.appliedRun.id}
                </code>
                <dl className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
                  <div>
                    <dt className="text-[11px] text-neutral-500">Apps</dt>
                    <dd className="mt-0.5 text-lg font-semibold tabular-nums text-white">
                      {number.format(page.appliedRun.appCount ?? 0)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] text-neutral-500">Attempt</dt>
                    <dd className="mt-0.5 text-lg font-semibold tabular-nums text-white">
                      {page.appliedRun.attempt}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] text-neutral-500">Applied</dt>
                    <dd className="mt-0.5 text-sm font-medium leading-7 text-white">
                      {page.appliedRun.appliedAt
                        ? formatShort(page.appliedRun.appliedAt)
                        : '–'}
                    </dd>
                  </div>
                </dl>
                <div className="mt-auto pt-4 text-xs">
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedRunId(page.appliedRun?.id ?? null)
                    }
                    className="inline-flex items-center gap-1 font-medium text-teal-300 transition-colors hover:text-teal-200"
                  >
                    Inspect run
                    <ArrowUpRight className="size-3.5" aria-hidden />
                  </button>
                </div>
              </div>
            ) : isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ) : (
              <p className="text-sm leading-6 text-neutral-500">
                Nothing is applied yet. Apply a computed run to publish an order
                to the Store.
              </p>
            )}
          </Panel>

          <Panel title="Pipeline">
            <div className="flex flex-1 flex-col gap-4">
              <SwitchRow
                id={computeLabelId}
                title="Enable dynamic ranking"
                description="Compute and persist a ranking after each scheduled analytics run."
                checked={enabled}
                disabled={saving}
                onChange={(next) => {
                  setEnabled(next);
                  if (!next) setApplyEnabled(false);
                }}
              />
              <SwitchRow
                id={applyLabelId}
                title="Apply rankings to Store"
                description="Push each new ranking to the target Store. Requires the writer endpoint and secret."
                checked={applyEnabled}
                disabled={!enabled || saving}
                onChange={setApplyEnabled}
              />
              <div className="mt-auto flex h-14 items-center justify-between gap-3 border-t border-white/[0.06] pt-4">
                {dirty ? (
                  <>
                    <span className="truncate text-xs font-medium text-amber-300">
                      Unsaved changes
                    </span>
                    <div className="flex items-center gap-2 text-sm">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={QUIET_BUTTON}
                        disabled={saving}
                        onClick={() => {
                          setEnabled(page.control.enabled);
                          setApplyEnabled(page.control.applyEnabled);
                        }}
                      >
                        Discard
                      </Button>
                      <Button
                        size="sm"
                        className={PRIMARY_BUTTON}
                        disabled={saving}
                        onClick={() => void saveControl()}
                      >
                        <StableLabel
                          options={['Save controls', 'Saving…']}
                          active={saving ? 'Saving…' : 'Save controls'}
                        />
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="line-clamp-2 text-xs leading-5 text-neutral-500">
                    Takes effect on the next scheduled analytics run. Last
                    changed {formatDate(page.control.updatedAt)}
                    {page.control.updatedByUserId
                      ? ` by ${page.control.updatedByUserId}`
                      : ''}
                    .
                  </p>
                )}
              </div>
            </div>
          </Panel>

          <Panel
            title="Fixed ranks"
            aside={
              <span className="text-xs tabular-nums text-neutral-500">
                {number.format(page.overrides.length)}
              </span>
            }
          >
            <div className="flex flex-1 flex-col">
              <div className="-mx-2 h-36 overflow-auto">
                {page.overrides.length === 0 ? (
                  <p className="px-2 text-sm leading-6 text-neutral-500">
                    No fixed ranks. Pin an app to hold a global position across
                    runs.
                  </p>
                ) : (
                  <ul className="space-y-0.5">
                    {page.overrides.map((override) => (
                      <li key={override.androidPackage}>
                        <button
                          type="button"
                          onClick={() => {
                            setOverridePackage(override.androidPackage);
                            setFixedRank(String(override.fixedRank));
                          }}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-white/[0.04]',
                            overridePackage === override.androidPackage &&
                              'bg-white/[0.06]',
                          )}
                        >
                          <span className="inline-flex w-10 shrink-0 items-center gap-0.5 font-mono text-xs tabular-nums text-amber-300">
                            <Pin className="size-3" aria-hidden />
                            {override.fixedRank}
                          </span>
                          <span className="min-w-0 flex-1 truncate font-mono text-xs text-neutral-200">
                            {override.androidPackage}
                          </span>
                          <span className="shrink-0 text-[11px] text-neutral-600">
                            {formatShort(override.updatedAt)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="mt-auto border-t border-white/[0.06] pt-4">
                <div className="flex gap-2 text-sm">
                  <input
                    type="text"
                    aria-label="Android package for fixed global rank"
                    placeholder="com.example.app"
                    value={overridePackage}
                    onInput={(event) =>
                      setOverridePackage(event.currentTarget.value)
                    }
                    className={cn(
                      FIELD,
                      'h-9 min-w-0 flex-1 px-3 font-mono text-xs',
                    )}
                  />
                  <input
                    type="number"
                    aria-label="Fixed global rank"
                    placeholder="Rank"
                    min={1}
                    step={1}
                    value={fixedRank}
                    onInput={(event) => setFixedRank(event.currentTarget.value)}
                    className={cn(FIELD, 'h-9 w-20 px-3 text-sm tabular-nums')}
                  />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                  <Button
                    size="sm"
                    className={PRIMARY_BUTTON}
                    disabled={savingOverride || !packageEntered || !rankValid}
                    onClick={() => void saveRankingOverride(Number(fixedRank))}
                  >
                    Save fixed rank
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={QUIET_BUTTON}
                    disabled={savingOverride || !packageEntered}
                    onClick={() => void saveRankingOverride(null)}
                  >
                    Clear
                  </Button>
                  <span className="ml-auto text-[11px] text-neutral-600">
                    Applies on the next snapshot
                  </span>
                </div>
              </div>
            </div>
          </Panel>
        </section>

        <section className="mt-6 grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
          <aside className="flex max-h-[50vh] flex-col self-start overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)]">
            <div className="border-b border-white/[0.06] px-4 pb-3 pt-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-white">
                  Runs
                  <span className="ml-1.5 text-xs font-normal tabular-nums text-neutral-500">
                    {number.format(page.runs.length)}
                  </span>
                </h2>
                {runInProgress && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-sky-300">
                    <span
                      aria-hidden
                      className="size-1.5 animate-pulse rounded-full bg-sky-400"
                    />
                    Computing
                  </span>
                )}
              </div>
              <div className="mt-3 flex gap-0.5 rounded-lg bg-white/[0.04] p-0.5 text-[11px]">
                {RUN_FILTERS.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilter(key)}
                    aria-current={filter === key}
                    className={cn(
                      'flex-1 rounded-md px-1.5 py-1 text-[11px] font-medium transition-colors',
                      filter === key
                        ? 'bg-white/10 text-white'
                        : 'text-neutral-500 hover:text-neutral-200',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="min-h-56 flex-1 overflow-y-auto p-2">
              {isLoading && page.runs.length === 0 ? (
                <div className="space-y-0.5">
                  {[0, 1, 2, 3].map((index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[8px_minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-0.5 px-3 py-2.5"
                    >
                      <Skeleton className="size-2 self-center rounded-full" />
                      <Skeleton className="h-5 w-2/3" />
                      <Skeleton className="h-3 w-10" />
                      <Skeleton className="col-span-2 col-start-2 h-4 w-1/2" />
                    </div>
                  ))}
                </div>
              ) : visibleRuns.length === 0 ? (
                <p className="flex min-h-52 items-center justify-center px-3 text-center text-sm text-neutral-500">
                  {page.runs.length === 0
                    ? 'No ranking runs yet.'
                    : 'No runs match this filter.'}
                </p>
              ) : (
                <div className="space-y-0.5">
                  {visibleRuns.map((run) => (
                    <RunRow
                      key={run.id}
                      run={run}
                      selected={selectedRunId === run.id}
                      live={page.appliedRun?.id === run.id}
                      onSelect={() => setSelectedRunId(run.id)}
                    />
                  ))}
                </div>
              )}
            </div>
            {page.nextCursor && (
              <div className="border-t border-white/[0.06] p-2 text-sm">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(QUIET_BUTTON, 'w-full')}
                  disabled={isLoading}
                  onClick={() => void loadRuns(page.nextCursor ?? undefined)}
                >
                  Load older runs
                </Button>
              </div>
            )}
          </aside>

          <div className="min-w-0 space-y-4">
            {detailError && <Note>{detailError}</Note>}
            {detailPending ? (
              <div className="space-y-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2">
                    <Skeleton className="h-7 w-44" />
                    <Skeleton className="h-3 w-72" />
                  </div>
                  <div className="flex gap-2">
                    <Skeleton className="h-8 w-28" />
                    <Skeleton className="h-8 w-44" />
                  </div>
                </div>
                <Skeleton className="h-10 w-80" />
                <Skeleton className="h-56 w-full rounded-2xl" />
                <div className="grid gap-4 xl:grid-cols-3">
                  <Skeleton className="h-60 rounded-2xl" />
                  <Skeleton className="h-60 rounded-2xl" />
                  <Skeleton className="h-60 rounded-2xl" />
                </div>
              </div>
            ) : runDetail ? (
              <div
                aria-busy={detailLoading}
                className={cn(
                  'transition-opacity duration-200',
                  detailLoading && 'pointer-events-none opacity-50',
                )}
              >
                <RunDetails
                  run={runDetail}
                  categories={categoryState}
                  applying={applyingRunId === runDetail.id}
                  live={page.appliedRun?.id === runDetail.id}
                  tab={tab}
                  onTabChange={setTab}
                  onApply={() => void applyRankingRun(runDetail)}
                />
              </div>
            ) : (
              <div className="flex h-64 items-center justify-center rounded-2xl border border-dashed border-white/[0.08]">
                <p className="text-sm text-neutral-500">
                  Select a run to inspect its ranking snapshot.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
